<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="modal d-block join-troubleshoot-modal"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('JoinTroubleshoot.Title')"
      @keydown.esc.prevent="close"
    >
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
          <div class="modal-header align-items-center">
            <h5 class="modal-title mb-0">{{ $t('JoinTroubleshoot.Title') }}</h5>
            <div class="d-flex align-items-center ml-auto">
              <button
                type="button"
                class="btn btn-sm btn-primary text-nowrap mr-2"
                :disabled="!canCopy"
                :aria-label="$t('JoinTroubleshoot.Copy results')"
                @click="copyResults"
              >
                <font-awesome-icon :icon="['fal', copied ? 'check' : 'copy']" class="mr-1" />
                {{ copied ? $t('JoinTroubleshoot.Copied') : $t('JoinTroubleshoot.Copy') }}
              </button>
              <button
                type="button"
                class="close ml-0"
                :aria-label="$t('Accessibility/General.Close')"
                @click="close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>

          <div class="modal-body">
            <p class="mb-3">{{ $t('JoinTroubleshoot.Intro') }}</p>

            <div v-if="isRunning && !checks.length" class="d-flex flex-column align-items-center py-4">
              <font-awesome-icon :icon="['fal', 'spinner']" class="font-size-px-40 mb-3" spin role="presentation" />
              <p class="mb-0">{{ $t('JoinTroubleshoot.Running') }}</p>
            </div>

            <div v-if="summaryText" class="alert mb-3" :class="summaryAlertClass" role="status">
              {{ summaryText }}
            </div>

            <ul class="list-unstyled mb-0">
              <li
                v-for="check in checks"
                :key="check.id"
                class="d-flex align-items-start py-2 border-bottom join-troubleshoot-row"
              >
                <span class="join-troubleshoot-status mr-3 mt-1" :class="'is-' + check.status" aria-hidden="true">
                  <font-awesome-icon :icon="statusIcons[check.status]" />
                </span>
                <div class="flex-grow-1 min-w-0">
                  <div class="font-weight-bold">{{ $t('JoinTroubleshoot.checks.' + check.id) }}</div>
                  <div class="font-size-px-14 mt-1">{{ detailText(check) }}</div>
                  <div v-if="check.suggestionKey && check.status !== 'pass'" class="font-size-px-14 mt-1 text-muted">
                    {{ $t('JoinTroubleshoot.suggestions.' + check.suggestionKey) }}
                  </div>
                </div>
              </li>
            </ul>
          </div>

          <div class="modal-footer flex-wrap">
            <button type="button" class="btn btn-secondary" :disabled="isRunning" @click="clearAppCachesAndReload">
              {{ $t('JoinTroubleshoot.Clear cache and reload') }}
            </button>
            <button type="button" class="btn btn-primary" :disabled="isRunning" @click="runChecks">
              <font-awesome-icon v-if="isRunning" :icon="['fal', 'spinner']" spin class="mr-1" role="presentation" />
              {{ isRunning ? $t('JoinTroubleshoot.Running') : $t('JoinTroubleshoot.Run again') }}
            </button>
            <button type="button" class="btn btn-secondary" @click="close">
              {{ $t('JoinTroubleshoot.Close') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import hotkeys from 'hotkeys-js'
import { runJoinTroubleshoot, clearAppCachesAndReload } from '../utils/joinTroubleshoot'

const SHORTCUT = 'command+option+shift+t,ctrl+alt+shift+t'
const STATUS_ICON = {
  pass: ['fal', 'check-circle'],
  warn: ['fal', 'triangle-exclamation'],
  fail: ['fal', 'times-circle']
}
const STATUS_LABEL = {
  pass: 'PASS',
  warn: 'WARN',
  fail: 'FAIL'
}

export default {
  name: 'JoinTroubleshootPanel',
  data () {
    return {
      isOpen: false,
      isRunning: false,
      checks: [],
      failCount: 0,
      warnCount: 0,
      hasRunError: false,
      runToken: 0,
      copied: false,
      copyResetTimer: null
    }
  },
  computed: {
    statusIcons () {
      return STATUS_ICON
    },
    canCopy () {
      return !this.isRunning && this.checks.length > 0
    },
    summaryAlertClass () {
      if (this.hasRunError || this.failCount > 0) return 'alert-danger'
      if (this.warnCount > 0) return 'alert-warning'
      if (this.checks.length) return 'alert-success'
      return ''
    },
    summaryText () {
      if (this.hasRunError) return this.$t('JoinTroubleshoot.Run failed')
      if (!this.checks.length) return ''
      if (this.failCount > 0) {
        return this.$t('JoinTroubleshoot.Summary failed', { count: this.failCount })
      }
      if (this.warnCount > 0) {
        return this.$t('JoinTroubleshoot.Summary warnings', { count: this.warnCount })
      }
      return this.$t('JoinTroubleshoot.Summary ok')
    }
  },
  methods: {
    clearAppCachesAndReload,
    detailText (check) {
      return this.$t('JoinTroubleshoot.details.' + check.detailKey, check.params || {})
    },
    clearCopyTimer () {
      if (this.copyResetTimer) {
        clearTimeout(this.copyResetTimer)
        this.copyResetTimer = null
      }
    },
    resetCopied () {
      this.copied = false
      this.clearCopyTimer()
    },
    buildReportText () {
      const lines = [
        this.$t('JoinTroubleshoot.Title'),
        new Date().toISOString(),
        window.location.href,
        navigator.userAgent || '',
        '',
        this.summaryText,
        ''
      ]

      this.checks.forEach((check) => {
        lines.push(`[${STATUS_LABEL[check.status] || check.status}] ${this.$t('JoinTroubleshoot.checks.' + check.id)}`)
        lines.push(`  ${this.detailText(check)}`)
        if (check.suggestionKey && check.status !== 'pass') {
          lines.push(`  ${this.$t('JoinTroubleshoot.suggestions.' + check.suggestionKey)}`)
        }
        lines.push('')
      })

      return lines.join('\n').trim() + '\n'
    },
    async writeClipboard (text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text)
        return
      }
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    },
    async copyResults () {
      if (!this.canCopy) return
      try {
        await this.writeClipboard(this.buildReportText())
        this.copied = true
        this.clearCopyTimer()
        this.copyResetTimer = setTimeout(() => {
          this.copied = false
          this.copyResetTimer = null
        }, 2000)
      } catch (error) {
        console.error('Failed to copy troubleshoot results', error)
      }
    },
    onShortcut (event) {
      event.preventDefault()
      this.open()
    },
    open () {
      this.isOpen = true
      this.resetCopied()
      this.runChecks()
    },
    close () {
      this.isOpen = false
      this.runToken += 1
      this.isRunning = false
      this.resetCopied()
    },
    async runChecks () {
      const token = ++this.runToken
      this.isRunning = true
      this.checks = []
      this.failCount = 0
      this.warnCount = 0
      this.hasRunError = false
      this.resetCopied()

      try {
        const result = await runJoinTroubleshoot({
          onProgress: (checks) => {
            if (token === this.runToken) this.checks = checks
          }
        })
        if (token !== this.runToken) return
        this.checks = result.checks
        this.failCount = result.failCount
        this.warnCount = result.warnCount
      } catch (error) {
        if (token !== this.runToken) return
        console.error('Join troubleshoot failed', error)
        this.hasRunError = true
        this.failCount = 1
        this.warnCount = 0
      } finally {
        if (token === this.runToken) this.isRunning = false
      }
    }
  },
  mounted () {
    hotkeys(SHORTCUT, this.onShortcut)
  },
  unmounted () {
    hotkeys.unbind(SHORTCUT, this.onShortcut)
    this.clearCopyTimer()
  }
}
</script>

<style lang="scss" scoped>
.join-troubleshoot-modal {
  background: rgba(0, 0, 0, 0.45);
  z-index: 1060;
}

.join-troubleshoot-status {
  width: 1.25rem;
  text-align: center;
  flex-shrink: 0;

  &.is-pass { color: #28a745; }
  &.is-warn { color: #e0a800; }
  &.is-fail { color: #dc3545; }
}

.join-troubleshoot-row:last-child {
  border-bottom: 0 !important;
}
</style>
